import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Users, Loader2, CheckCircle2 } from "lucide-react";

export default function JoinTeam() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    localStorage.setItem("pending_team_invite", token);
    api.get(`/public/team-invites/${token}`)
      .then((r) => setInvite(r.data))
      .catch(() => setErr("This invite link is invalid or has expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const accept = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/team-invites/${token}/accept`);
      localStorage.removeItem("pending_team_invite");
      setDone(true);
      setTimeout(() => nav("/founder/team"), 1600);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't accept the invite");
    } finally { setBusy(false); }
  };

  const card = "mx-auto mt-16 w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-8 text-center shadow-sm";
  return (
    <div className="min-h-screen bg-[#F4F8F8] px-4" style={{ fontFamily: "Manrope, sans-serif" }}>
      {loading || authLoading ? (
        <div className={card} data-testid="join-team-loading">
          <Loader2 className="mx-auto h-7 w-7 animate-spin text-[#0A8B85]" />
        </div>
      ) : err && !invite ? (
        <div className={card} data-testid="join-team-invalid">
          <p className="text-lg font-bold text-neutral-900">Invite not found</p>
          <p className="mt-2 text-sm text-neutral-600">{err}</p>
          <Link to="/" className="mt-5 inline-block text-sm font-bold text-[#0A8B85]">Go to Clinq</Link>
        </div>
      ) : (
        <div className={card} data-testid="join-team-card">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#0A8B85]/10">
            <Users className="h-6 w-6 text-[#0A8B85]" />
          </div>
          <h1 className="mt-4 text-xl font-bold text-neutral-900">
            Join {invite.company_name} on Clinq
          </h1>
          <p className="mt-2 text-sm text-neutral-600">
            <b>{invite.invited_by_name}</b> invited you to collaborate as{" "}
            <b>{invite.business_role}</b> on their capital raise.
          </p>

          {invite.status !== "pending" ? (
            <p className="mt-5 rounded-lg bg-neutral-100 px-4 py-3 text-sm font-semibold text-neutral-600"
               data-testid="join-team-used">
              This invite has already been {invite.status}.
            </p>
          ) : done ? (
            <p className="mt-5 flex items-center justify-center gap-2 rounded-lg bg-green-50 px-4 py-3 text-sm font-bold text-green-700"
               data-testid="join-team-success">
              <CheckCircle2 className="h-4 w-4" /> You're on the team — taking you there…
            </p>
          ) : user ? (
            user.role === "founder" ? (
              <>
                {err && <p className="mt-4 text-sm font-semibold text-red-500" data-testid="join-team-error">{err}</p>}
                <button onClick={accept} disabled={busy} data-testid="join-team-accept-btn"
                  className="mt-5 w-full rounded-lg bg-[#0A8B85] py-3 text-sm font-bold text-white hover:bg-[#066667] disabled:opacity-50">
                  {busy ? "Joining…" : `Accept & join ${invite.company_name}`}
                </button>
                <p className="mt-3 text-xs text-neutral-500">Signed in as {user.email}</p>
              </>
            ) : (
              <p className="mt-5 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="join-team-wrong-role">
                You're signed in with a {user.role} account. Team invites are for founder accounts —
                log out and sign in (or sign up) as a founder.
              </p>
            )
          ) : (
            <div className="mt-6 space-y-2">
              <Link to={`/signup?role=founder`} data-testid="join-team-signup-btn"
                className="block w-full rounded-lg bg-[#0A8B85] py-3 text-sm font-bold text-white hover:bg-[#066667]">
                Create a free account to join
              </Link>
              <Link to={`/login?redirect=/join-team/${token}`} data-testid="join-team-login-btn"
                className="block w-full rounded-lg border border-neutral-200 bg-white py-3 text-sm font-bold text-neutral-800 hover:bg-neutral-50">
                I already have an account — log in
              </Link>
              <p className="text-xs text-neutral-500">Invite sent to {invite.email}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
